import React, { Suspense, type ReactNode } from 'react';

import { Icon, IconKey } from '@/shared/components/icon';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { cn } from '@/shared/utils';

import { QueryErrorBoundary } from '../query-error-boundary';

// ── Tab compound child ────────────────────────────────────────────────

interface TabProps {
  value: string;
  label: string;
  className?: string;
  children: ReactNode;
}

/**
 * Declarative tab definition — does not render on its own.
 * `DetailDrawer` reads each Tab's props to build triggers + content panels.
 */
function Tab(_props: TabProps): React.JSX.Element | null {
  return null;
}

Tab.displayName = 'DetailDrawer.Tab';

// ── Main component ───────────────────────────────────────────────────

type DrawerSize = 'default' | 'sm';

const DRAWER_SIZE_CLASS: Record<DrawerSize, string> = {
  default: 'sm:max-w-7xl',
  sm: 'sm:max-w-3xl',
};

interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  backLabel: string;
  title: string;
  description: string;
  loading?: boolean;
  loadingFallback?: ReactNode;
  header: ReactNode;
  headerClassName?: string;
  defaultTab?: string;
  size?: DrawerSize;
  children: ReactNode;
}

const TAB_TRIGGER_CLASS =
  'rounded-none border-b-2 border-transparent px-1 pb-2.5 pt-2 text-muted-foreground data-[state=active]:border-accent data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none -mb-px';

const TAB_CONTENT_BASE = 'p-4 pt-2';

function TabSuspenseFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <Skeleton className="h-6 w-6 rounded-full" />
    </div>
  );
}

function DetailDrawerRoot({
  open,
  onOpenChange,
  backLabel,
  title,
  description,
  loading = false,
  loadingFallback,
  header,
  headerClassName = 'mb-8',
  defaultTab,
  size = 'default',
  children,
}: DetailDrawerProps) {
  // Extract tab definitions from children
  const tabs: TabProps[] = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement<TabProps>(child) && child.type === Tab) {
      tabs.push(child.props);
    }
  });

  const resolvedDefaultTab = defaultTab ?? tabs[0]?.value;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" hideClose className={cn('flex flex-col p-0 overflow-hidden bg-card', DRAWER_SIZE_CLASS[size])}>
        <div className="px-4 pt-3 pb-1">
          <SheetClose className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors hover:cursor-pointer">
            <Icon icon={IconKey.ArrowLeft} size={14} />
            <span>{backLabel}</span>
          </SheetClose>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <>
              <SheetHeader className="p-4">
                <SheetTitle>Loading...</SheetTitle>
                <SheetDescription className="sr-only">Loading...</SheetDescription>
              </SheetHeader>
              {loadingFallback}
            </>
          ) : (
            <>
              <SheetHeader className={cn('p-4 text-left', headerClassName)}>
                <SheetTitle className="sr-only">{title}</SheetTitle>
                <SheetDescription className="sr-only">{description}</SheetDescription>
                {header}
              </SheetHeader>

              <Tabs defaultValue={resolvedDefaultTab} className="px-4">
                <TabsList className="w-full justify-start rounded-none bg-transparent px-4 py-0 mb-3 border-b border-border gap-6 h-auto">
                  {tabs.map((tab) => (
                    <TabsTrigger key={tab.value} value={tab.value} className={TAB_TRIGGER_CLASS}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {tabs.map((tab) => (
                  <TabsContent
                    key={tab.value}
                    value={tab.value}
                    className={cn(TAB_CONTENT_BASE, tab.className)}
                  >
                    <QueryErrorBoundary>
                      <Suspense fallback={<TabSuspenseFallback />}>
                        {tab.children}
                      </Suspense>
                    </QueryErrorBoundary>
                  </TabsContent>
                ))}
              </Tabs>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export type { DrawerSize };
export const DetailDrawer = Object.assign(DetailDrawerRoot, { Tab });
